<script>
import { mapGetters, mapState } from 'vuex';
import AgentBotModal from './AgentBotModal.vue';
import AgentBotRow from './AgentBotRow.vue';
import BaseSettingsHeader from '../../components/BaseSettingsHeader.vue';
import globalConfigMixin from 'shared/mixins/globalConfigMixin';
import { useAlert } from 'dashboard/composables';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

export default {
  components: {
    AgentBotRow,
    AgentBotModal,
    BaseSettingsHeader,
  },
  mixins: [globalConfigMixin],
  data() {
    return {
      loading: {},
      isPageLoading: false,
      showAgentBotPopup: false,
      showDeleteConfirmationPopup: false,
      selectedApp: {},
      selectedInbox: {},
      mode: 'CREATE',
    };
  },
  computed: {
    ...mapGetters({
      inboxes: 'inboxes/getInboxes',
      agentBots: 'agentBots/getBots',
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
    }),
    ...mapState({
      agentBotInbox: state => state.agentBots.agentBotInbox,
    }),
    integration() {
      return this.$store.getters['integrations/getIntegration']('agent_bots');
    },
    isAgentBotsFeatureEnabled() {
      return this.isFeatureEnabledonAccount(
        this.accountId,
        FEATURE_FLAGS.AGENT_BOTS
      );
    },
  },
  watch: {
    inboxes: {
      immediate: true,
      handler(newInboxes) {
        if (newInboxes.length && !this.showAgentBotPopup) {
          this.fetchAgentBotStatuses();
        }
      },
    },
  },
  mounted() {
    this.loadPageData();
  },
  methods: {
    async loadPageData() {
      this.isPageLoading = true;
      try {
        await this.$store.dispatch('inboxes/get');
        await this.$store.dispatch('agentBots/get', { setLoading: false });
        await this.fetchAgentBotStatuses();
      } catch (error) {
        useAlert(
          this.$t('INTEGRATION_SETTINGS.AGENT_BOTS.FAILED_TO_LOAD_PAGE')
        );
      } finally {
        this.isPageLoading = false;
      }
    },
    hasAgentBot(inboxId) {
      return !!this.agentBotInbox[inboxId];
    },
    async fetchAgentBotStatuses() {
      try {
        this.$nextTick(async () => {
          if (this.inboxes.length) {
            await Promise.all(
              this.inboxes.map(inbox =>
                this.$store.dispatch('agentBots/fetchAgentBotInbox', inbox.id)
              )
            );
          }
        });
      } catch (error) {
        useAlert(
          this.$t(
            'INTEGRATION_SETTINGS.AGENT_BOTS.FAILED_TO_FETCH_AGENT_BOT_STATUSES'
          )
        );
      }
    },
    openCreatePopup(inbox) {
      this.selectedInbox = inbox;
      this.showAgentBotPopup = true;
    },
    toggleDashboardAppPopup() {
      this.showAgentBotPopup = !this.showAgentBotPopup;
      if (!this.showAgentBotPopup) {
        // Reset selected inbox when closing
        this.selectedInbox = {};
      }
    },
    onToggleAgentBot({ inboxId, enabled }) {
      this.$store.dispatch('agentBots/toggle', {
        inboxId,
        enabled,
      });
    },
  },
};
</script>

<template>
  <div class="flex flex-col flex-1 gap-8 overflow-auto">
    <BaseSettingsHeader
      v-if="integration.name"
      :title="integration.name"
      :description="integration.description"
      feature-name="agent_bots"
      :back-button-label="$t('INTEGRATION_SETTINGS.HEADER')"
    />
    <div class="w-full overflow-x-auto text-slate-700 dark:text-slate-200">
      <woot-loading-state
        v-if="isPageLoading"
        :message="$t('AGENT_BOTS.LIST.LOADING')"
      />
      <template v-else-if="isAgentBotsFeatureEnabled">
        <table v-if="agentBots.length" class="woot-table">
          <tbody>
            <AgentBotRow
              v-for="(inbox, index) in inboxes"
              :key="inbox.id"
              :inbox="inbox"
              :index="index"
              :has-agent-bot="hasAgentBot(inbox.id)"
              @open-create-popup="openCreatePopup"
            />
          </tbody>
        </table>
        <p v-else class="flex flex-col items-center justify-center h-full">
          {{ $t('INTEGRATION_SETTINGS.AGENT_BOTS.LIST.404') }}
        </p>
      </template>
      <div v-else class="text-center p-4">
        <p>{{ $t('INTEGRATION_SETTINGS.AGENT_BOTS.FEATURE_DISABLED') }}</p>
      </div>
    </div>

    <AgentBotModal
      v-if="showAgentBotPopup"
      :show="showAgentBotPopup"
      :selected-inbox="selectedInbox"
      @close="toggleDashboardAppPopup"
    />
  </div>
</template>

<style scoped>
.bots-list {
  list-style: none;
}

.nowrap {
  white-space: nowrap;
}

.white-text {
  color: white;
}
</style>
